import { decamelizeKeys } from "humps";
import isEqual from "lodash/isEqual";
import { ProcessingOverlay } from "PFApp/components/activity_form/multi_activity_form/processing_overlay";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  ManageRolesStoreProvider,
  useManageRolesStore,
  useSyncManageRolesAvailableColumns
} from "PFApp/stores/manage_roles";
import { useManageViewRoles } from "PFCore/hooks/queries/roles/use_manage_view_roles";
import { AvailabilityMode } from "PFCore/types/booking";
import { ManageRolesViewRoleItem } from "PFCore/types/manage_roles_view";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useUnmount } from "react-use";

import { useManageRolesBulkFillAndBook } from "./hooks/use_manage_roles_bulk_fill_and_book";
import { parseJsonArrayIds } from "./manage_roles.utils";
import { MAX_ENGAGEMENT_IDS, MAX_ROLE_IDS, ROLES_PER_PAGE } from "./manage_roles_constants";
import { ManageRolesPageContent } from "./manage_roles_page";
import { ManageRolesFormProvider } from "./parts/manage_roles_form_provider";
import { SuggestedWMSidePanel } from "./parts/suggested_wm_side_panel";
import { useSuggestedWmSidePanelActions } from "./parts/suggested_wm_side_panel/use_suggested_wm_side_panel_actions";

const ROLE_IDS_QUERY_PARAM = "roleIds";
const ENGAGEMENT_IDS_QUERY_PARAM = "engagementIds";
const NEW_ROLES_ORDER_NAME = ["activity.subtemplate_key", "activity.saas_updated_at"];

const ManageRolesPageInner = () => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const currentPage = useManageRolesStore((state) => state.currentPage);
  const activityIds = useManageRolesStore((state) => state.activityIds);
  const newActivityIds = useManageRolesStore((state) => state.newActivityIds);
  const newDraftRoles = useManageRolesStore((state) => state.newDraftRoles);
  const { closeSuggestedWmSidePanel } = useSuggestedWmSidePanelActions();

  useSyncManageRolesAvailableColumns();
  useUnmount(closeSuggestedWmSidePanel);

  const { mutate: bulkFillAndBook, isProcessing: isFillAndBookProcessing } = useManageRolesBulkFillAndBook();

  // newActivityIds are added to the array to get the correct "total" count
  // they are then filtered out from the entries to not display duplicates
  const activityIdsWithNewRoles = [...activityIds, ...newActivityIds];

  const { data, isLoading } = useManageViewRoles(
    {
      targetAvailabilityMode: AvailabilityMode.PerWeek,
      page: currentPage,
      perPage: ROLES_PER_PAGE,
      ...(activityIdsWithNewRoles.length > 0 ? { activityIds: activityIdsWithNewRoles } : {})
    },
    {
      select: (data) => ({
        entries: decamelizeKeys(data.entries) as any,
        meta: data.meta
      })
    }
  );

  const newRolesOrder = data?.meta.order?.options.find(({ name }) => isEqual(name, NEW_ROLES_ORDER_NAME));
  const { data: newData, isLoading: isNewDataLoading } = useManageViewRoles(
    {
      targetAvailabilityMode: AvailabilityMode.PerWeek,
      perPage: PER_PAGE_UNPAGINATED,
      activityIds: newActivityIds,
      order: newRolesOrder
    },
    {
      enabled: newActivityIds.length > 0 && !!data,
      select: (data) => ({
        entries: decamelizeKeys(data.entries) as any,
        meta: data.meta
      })
    }
  );

  const entries: ManageRolesViewRoleItem[] = useMemo(() => {
    const dataEntries: ManageRolesViewRoleItem[] = data?.entries ?? [];
    const newEntries: ManageRolesViewRoleItem[] = newData?.entries ?? [];
    const newActivityIdSet = new Set(newActivityIds);
    return [...newEntries, ...dataEntries.filter((entry) => !newActivityIdSet.has(entry.id))];
  }, [data, newData, newActivityIds]);
  const totalPages = data?.meta.totalPages ?? 1;
  const totalRoles = (data?.meta.total ?? 0) + newDraftRoles.length;
  const fillableVacanciesCount = data?.meta.counters?.fillAndBookableVacancies ?? 0;

  return (
    <>
      {(isLoading || isNewDataLoading) && (
        <ProcessingOverlay title={t("preparingRoles")} subtitle={t("preparingRolesDescription")} />
      )}
      <ManageRolesFormProvider entries={entries}>
        <ManageRolesPageContent
          totalRoles={totalRoles}
          totalPages={totalPages}
          fillableVacanciesCount={fillableVacanciesCount}
          isFillAndBookProcessing={isFillAndBookProcessing}
          onFillAndBook={() => bulkFillAndBook({ activityIds: activityIdsWithNewRoles as number[] })}
          isLoading={isLoading || isNewDataLoading}
        />
      </ManageRolesFormProvider>
      <SuggestedWMSidePanel />
    </>
  );
};

export const ManageRolesPageController = () => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });
  const { showGrowl } = useGrowlStoreActions();

  const [searchParams] = useSearchParams();
  const rawRoleIds = parseJsonArrayIds(searchParams.get(ROLE_IDS_QUERY_PARAM));
  const rawEngagementIds = parseJsonArrayIds(searchParams.get(ENGAGEMENT_IDS_QUERY_PARAM));

  const roleIdsTruncated = rawRoleIds.length > MAX_ROLE_IDS;
  const engagementIdsTruncated = rawEngagementIds.length > MAX_ENGAGEMENT_IDS;

  useEffect(() => {
    if (roleIdsTruncated || engagementIdsTruncated) {
      showGrowl({ message: t("paramsTruncated"), kind: "alert" });
    }
  }, [roleIdsTruncated, engagementIdsTruncated, showGrowl, t]);

  const engagementIds = rawEngagementIds.slice(0, MAX_ENGAGEMENT_IDS);
  const roleIds = rawRoleIds.slice(0, MAX_ROLE_IDS);
  const activityIds = [...engagementIds, ...roleIds];

  return (
    <ManageRolesStoreProvider initialActivityIds={activityIds}>
      <ManageRolesPageInner />
    </ManageRolesStoreProvider>
  );
};
